import React, { useCallback, useEffect, useState } from "react";
import { buildApiUrl } from "../utils/apiConfig";
import { redirectToLogin } from "../utils/auth";
import { athleteProfileFetchPath } from "../utils/viewedAthlete";
import { formatUsDate } from "../utils/completeByDate";
import Pagination from "./Pagination";
import DeleteTaskModal from "./DeleteTaskModal";
import NilDealModal, { NilDealModalMode } from "./NilDealModal";
import {
    deleteNilDeal,
    fetchNilDeals,
    getNilDealAgreementUrl,
    NilDealDto,
} from "../services/nilDealsService";
import EyeIcon from "../images/buttons/Eye.svg";
import EditIcon from "../images/buttons/Edit.svg";
import DeleteIcon from "../images/buttons/Delete.svg";
import "../styles/nil-deals.css";
import "../styles/pagination.css";

interface NilDealsTabProps {
    athleteProfileId?: string;
    canMutate: boolean;
}

const PAGE_SIZE = 10;

export default function NilDealsTab({ athleteProfileId, canMutate }: NilDealsTabProps) {
    const [athleteUserId, setAthleteUserId] = useState<number | undefined>();
    const [deals, setDeals] = useState<NilDealDto[]>([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState<NilDealModalMode>("create");
    const [selectedDeal, setSelectedDeal] = useState<NilDealDto | null>(null);

    const [deleteTarget, setDeleteTarget] = useState<NilDealDto | null>(null);
    const [deleting, setDeleting] = useState(false);

    const resolveAthleteUserId = useCallback(async (): Promise<number | undefined> => {
        const profilePath = athleteProfileFetchPath(athleteProfileId);
        if (!profilePath) {
            return undefined;
        }
        const res = await fetch(buildApiUrl(profilePath), {
            credentials: "include",
            headers: { Accept: "application/json" },
        });
        if (res.status === 401 || res.status === 403) {
            redirectToLogin(true);
            return undefined;
        }
        if (res.ok) {
            const profile = await res.json();
            return profile.userId ?? profile.id;
        }
        return undefined;
    }, [athleteProfileId]);

    const loadDeals = useCallback(
        async (page: number) => {
            try {
                setLoading(true);
                setError(null);
                const userId = await resolveAthleteUserId();
                setAthleteUserId(userId);
                const result = await fetchNilDeals(page, PAGE_SIZE, userId);
                setDeals(result.content);
                setTotalPages(result.totalPages);
                setTotalElements(result.totalElements);
                // If the requested page is now empty (e.g. after a delete), step back.
                if (result.content.length === 0 && page > 0 && result.totalPages > 0) {
                    setCurrentPage(Math.min(page - 1, result.totalPages - 1));
                }
            } catch (e: unknown) {
                setError(e instanceof Error ? e.message : "Failed to load NIL deals");
            } finally {
                setLoading(false);
            }
        },
        [resolveAthleteUserId]
    );

    useEffect(() => {
        loadDeals(currentPage);
    }, [loadDeals, currentPage]);

    const handleAdd = () => {
        setSelectedDeal(null);
        setModalMode("create");
        setModalOpen(true);
    };

    const handleView = (deal: NilDealDto) => {
        setSelectedDeal(deal);
        setModalMode("view");
        setModalOpen(true);
    };

    const handleEdit = (deal: NilDealDto) => {
        setSelectedDeal(deal);
        setModalMode("edit");
        setModalOpen(true);
    };

    const handleSaved = () => {
        // No full page refresh — reload just the current page of the table.
        loadDeals(currentPage);
    };

    const handleConfirmDelete = async () => {
        if (!deleteTarget) {
            return;
        }
        try {
            setDeleting(true);
            await deleteNilDeal(deleteTarget.id, athleteUserId);
            setDeleteTarget(null);
            await loadDeals(currentPage);
        } catch (e: unknown) {
            setError(e instanceof Error ? e.message : "Failed to delete NIL deal");
        } finally {
            setDeleting(false);
        }
    };

    const handleViewDocument = async (deal: NilDealDto) => {
        try {
            const { url } = await getNilDealAgreementUrl(deal.id, athleteUserId);
            window.open(url, "_blank", "noopener,noreferrer");
        } catch {
            setError("We couldn’t open the agreement document. Please try again.");
        }
    };

    // When an admin/staff user manages a specific athlete (athleteProfileId set), every mutation must
    // carry that athlete's resolved userId. Resolving it requires a profile fetch, so block "Add"
    // until it is available — otherwise a fast click would POST without athleteUserId and the API
    // would reject it ("athleteUserId is required for non-athlete users."). Athletes managing their
    // own deals have no athleteProfileId and the backend infers them from the session, so they are
    // only gated by the initial load.
    const awaitingAthleteUserId = Boolean(athleteProfileId) && athleteUserId == null;
    const addDisabled = loading || awaitingAthleteUserId;

    return (
        <section className="nil-deals-section" data-testid="nil-deals-panel">
            <div className="nil-deals-card">
                <div className="nil-deals-header">
                    <h2>Opportunities</h2>
                    {canMutate && (
                        <button
                            type="button"
                            className="nil-deals-add-btn"
                            onClick={handleAdd}
                            disabled={addDisabled}
                            data-testid="nil-deals-add-btn"
                        >
                            + Add NIL Deal
                        </button>
                    )}
                </div>

                {error && (
                    <div className="nil-deals-error" role="alert" data-testid="nil-deals-error">
                        {error}
                    </div>
                )}

                {loading ? (
                    <div className="nil-deals-loading" data-testid="nil-deals-loading">Loading NIL deals…</div>
                ) : deals.length === 0 ? (
                    <div className="nil-deals-empty" data-testid="nil-deals-empty">
                        No NIL deals yet.
                        {canMutate ? " Click “Add NIL Deal” to record your first one." : ""}
                    </div>
                ) : (
                    <div className="nil-deals-table-wrap">
                        <table className="nil-deals-table">
                            <thead>
                                <tr>
                                    <th className="nil-deals-col-name">Deal Name</th>
                                    <th className="nil-deals-col-desc">Description</th>
                                    <th className="nil-deals-col-date">Date Submitted</th>
                                    <th className="nil-deals-col-agreement">Agreement</th>
                                    <th className="nil-deals-col-actions">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {deals.map((deal) => (
                                    <tr key={deal.id} data-testid={`nil-deal-row-${deal.id}`}>
                                        <td className="nil-deals-col-name" data-label="Deal Name">
                                            <span className="nil-deals-deal-name">{deal.dealName}</span>
                                        </td>
                                        <td className="nil-deals-col-desc" data-label="Description">
                                            {deal.description}
                                        </td>
                                        <td className="nil-deals-col-date" data-label="Date Submitted">
                                            {formatUsDate(deal.dateSubmitted)}
                                        </td>
                                        <td className="nil-deals-col-agreement" data-label="Agreement">
                                            {deal.hasAgreement ? (
                                                <button
                                                    type="button"
                                                    className="nil-deals-view-document"
                                                    onClick={() => handleViewDocument(deal)}
                                                    data-testid={`nil-deal-view-document-${deal.id}`}
                                                >
                                                    View document
                                                </button>
                                            ) : (
                                                <span className="nil-deals-no-link">No agreement uploaded</span>
                                            )}
                                        </td>
                                        <td className="nil-deals-col-actions" data-label="Actions">
                                            <div className="nil-deals-actions">
                                                <button
                                                    type="button"
                                                    className="nil-deals-action-btn"
                                                    title="View deal"
                                                    aria-label={`View ${deal.dealName}`}
                                                    onClick={() => handleView(deal)}
                                                    data-testid={`nil-deal-view-${deal.id}`}
                                                >
                                                    <img src={EyeIcon} alt="" />
                                                </button>
                                                {canMutate && (
                                                    <>
                                                        <button
                                                            type="button"
                                                            className="nil-deals-action-btn"
                                                            title="Edit deal"
                                                            aria-label={`Edit ${deal.dealName}`}
                                                            onClick={() => handleEdit(deal)}
                                                            data-testid={`nil-deal-edit-${deal.id}`}
                                                        >
                                                            <img src={EditIcon} alt="" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="nil-deals-action-btn"
                                                            title="Delete deal"
                                                            aria-label={`Delete ${deal.dealName}`}
                                                            onClick={() => setDeleteTarget(deal)}
                                                            data-testid={`nil-deal-delete-${deal.id}`}
                                                        >
                                                            <img src={DeleteIcon} alt="" />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {!loading && totalPages > 1 && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalElements={totalElements}
                        pageSize={PAGE_SIZE}
                        onPageChange={setCurrentPage}
                    />
                )}
            </div>

            <NilDealModal
                open={modalOpen}
                mode={modalMode}
                deal={selectedDeal}
                athleteUserId={athleteUserId}
                onClose={() => setModalOpen(false)}
                onSaved={handleSaved}
            />

            <DeleteTaskModal
                open={deleteTarget != null}
                onClose={() => (deleting ? undefined : setDeleteTarget(null))}
                onConfirm={handleConfirmDelete}
                taskTitle={deleteTarget?.dealName}
                itemLabel="NIL deal"
            />
        </section>
    );
}
